import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { areYouSure } from 'lib/utils/toast';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import { Button, Col, FormControl, InputGroup, Row } from 'react-bootstrap';
import Container from 'components/shared/Container';
import { css, StyleSheet } from 'aphrodite';
import { grey, greyBackground } from 'lib/css/colors';

function DuplicateBrandsActionsSection({
  updateBrands,
  mergeOrDeleteList,
  setShowMergeModal,
  refetchDuplicateBrandClusters,
  refetchDuplicateBrandClustersCount,
  handleMarkClusterAsCompleted,
  currentPage,
  duplicateBrandClustersCount,
  loadingDuplicateBrandClusters,
  setCurrentPage,
  setMergeOrDeleteList,
}) {
  const [inputValue, setInputValue] = useState(String(currentPage));

  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const handleGoToPage = () => {
    const parsed = parseInt(inputValue, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > duplicateBrandClustersCount) {
      setInputValue(String(currentPage));
      return;
    }
    if (parsed !== currentPage) {
      setCurrentPage(parsed);
      setMergeOrDeleteList([]);
    }
  };

  return (
    <Container>
      <Row className="d-flex mb-2 justify-content-between">
        <div className="d-flex">
          <Button
            className="mr-2 ml-3"
            variant="primary"
            onClick={() => {
              updateBrands({
                brand_ids: mergeOrDeleteList.map(brand => brand.id),
                verified: BrandVerificationStatus.UNVERIFIED,
              });
            }}
            disabled={mergeOrDeleteList.length < 1}
          >
            Unverify selected brands
          </Button>
          <Button
            className="mr-2 ml"
            variant="primary"
            onClick={() => setShowMergeModal(true)}
            disabled={mergeOrDeleteList.length < 2}
          >
            Merge Brands
          </Button>
          <Button
            className="mr-3"
            variant="outline-primary"
            onClick={() => {
              refetchDuplicateBrandClusters();
              refetchDuplicateBrandClustersCount();
            }}
          >
            Refresh
          </Button>
        </div>

        <Button
          className="mr-3"
          variant="primary"
          onClick={() => {
            areYouSure(() => {
              handleMarkClusterAsCompleted();
            }, 'Are you sure you want to mark this cluster as completed?');
          }}
        >
          Mark Group as Completed
        </Button>
      </Row>
      <Row>
        <Col className="d-flex justify-content-end align-items-center mb-2">
          <span className={css(styles.paginationLabel)}>Group</span>
          <InputGroup size="sm" className={css(styles.paginationGroup)}>
            <InputGroup.Prepend>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setCurrentPage(currentPage - 1);
                  setMergeOrDeleteList([]);
                }}
                disabled={
                  loadingDuplicateBrandClusters ||
                  currentPage === 1 ||
                  duplicateBrandClustersCount === 0
                }
              >
                &#8249;
              </Button>
            </InputGroup.Prepend>
            <FormControl
              className={css(styles.pageInput)}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              onBlur={handleGoToPage}
              disabled={loadingDuplicateBrandClusters || duplicateBrandClustersCount === 0}
            />
            <InputGroup.Append>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setCurrentPage(currentPage + 1);
                  setMergeOrDeleteList([]);
                }}
                disabled={
                  loadingDuplicateBrandClusters ||
                  currentPage === duplicateBrandClustersCount ||
                  duplicateBrandClustersCount === 0
                }
              >
                &#8250;
              </Button>
            </InputGroup.Append>
          </InputGroup>
          <span className={css(styles.paginationLabel)}>
            of {duplicateBrandClustersCount} groups
          </span>
        </Col>
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: greyBackground,
    },
  },
  paginationGroup: {
    width: 'auto',
    flexWrap: 'nowrap',
  },
  pageInput: {
    width: '52px',
    textAlign: 'center',
    padding: '0.25rem 0.4rem',
    MozAppearance: 'textfield',
    '::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
  paginationLabel: {
    fontSize: '0.875rem',
    color: grey,
    whiteSpace: 'nowrap',
    margin: '0 8px',
  },
});

DuplicateBrandsActionsSection.propTypes = {
  updateBrands: PropTypes.func.isRequired,
  mergeOrDeleteList: PropTypes.array.isRequired,
  setShowMergeModal: PropTypes.func.isRequired,
  refetchDuplicateBrandClusters: PropTypes.func.isRequired,
  refetchDuplicateBrandClustersCount: PropTypes.func.isRequired,
  handleMarkClusterAsCompleted: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  duplicateBrandClustersCount: PropTypes.number.isRequired,
  loadingDuplicateBrandClusters: PropTypes.bool.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setMergeOrDeleteList: PropTypes.func.isRequired,
};

export default DuplicateBrandsActionsSection;
